import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import travelObjectTenantSettingsObject from '@/entities/travelObjectTenantSettings/interface'
import travelHighlightObject from '@/entities/travelHighlight/interface'
import travelPartnerObject from '@/entities/travelPartner/interface'
import travelGuideObject from '@/entities/travelGuide/interface'
import travelTeamObject from '@/entities/travelTeam/interface'
import travelAccommodationAssignmentObject from '@/entities/travelAccommodationAssignment/interface'
import accommodationObject from '@/entities/accommodation/interface'
import travelDestinationObject from '@/entities/travelDestination/interface'
import travelTestimonialObject from '@/entities/travelTestimonial/interface'
import travelExtensionObject from '@/entities/travelExtension/interface'
import travelFaqObject from '@/entities/travelFaq/interface'

import {
  ALL_TRAVELS_QUERY,
  SINGLE_TRAVEL_QUERY,
  TRAVELS_DEPENDENTS_QUERY,
  CREATE_TRAVEL_MUTATION,
  UPDATE_TRAVEL_MUTATION,
  DELETE_TRAVEL_MUTATION,
  FORCE_DELETE_TRAVEL_MUTATION,
  ADD_TRAVEL_TENANT_SETTINGS_MUTATION,
  REMOVE_TRAVEL_TENANT_SETTINGS_MUTATION,
  CREATE_TRAVEL_TENANT_SETTINGS_MUTATION,
  DELETE_TRAVEL_TENANT_SETTINGS_MUTATION,
  UPDATE_TRAVEL_TENANT_SETTINGS_ORDER_MUTATION,
  ADD_TRAVEL_HIGHLIGHTS_MUTATION,
  REMOVE_TRAVEL_HIGHLIGHTS_MUTATION,
  CREATE_TRAVEL_HIGHLIGHTS_MUTATION,
  DELETE_TRAVEL_HIGHLIGHTS_MUTATION,
  UPDATE_TRAVEL_HIGHLIGHTS_ORDER_MUTATION,
  ADD_TRAVEL_PARTNERS_MUTATION,
  REMOVE_TRAVEL_PARTNERS_MUTATION,
  CREATE_TRAVEL_PARTNERS_MUTATION,
  DELETE_TRAVEL_PARTNERS_MUTATION,
  UPDATE_TRAVEL_PARTNERS_ORDER_MUTATION,
  ADD_TRAVEL_TRAVEL_GUIDES_MUTATION,
  REMOVE_TRAVEL_TRAVEL_GUIDES_MUTATION,
  CREATE_TRAVEL_TRAVEL_GUIDES_MUTATION,
  DELETE_TRAVEL_TRAVEL_GUIDES_MUTATION,
  UPDATE_TRAVEL_TRAVEL_GUIDES_ORDER_MUTATION,
  ADD_TRAVEL_TEAMS_MUTATION,
  REMOVE_TRAVEL_TEAMS_MUTATION,
  CREATE_TRAVEL_TEAMS_MUTATION,
  DELETE_TRAVEL_TEAMS_MUTATION,
  UPDATE_TRAVEL_TEAMS_ORDER_MUTATION,
  ADD_TRAVEL_ACCOMMODATION_ASSIGNMENTS_MUTATION,
  REMOVE_TRAVEL_ACCOMMODATION_ASSIGNMENTS_MUTATION,
  CREATE_TRAVEL_ACCOMMODATION_ASSIGNMENTS_MUTATION,
  DELETE_TRAVEL_ACCOMMODATION_ASSIGNMENTS_MUTATION,
  UPDATE_TRAVEL_ACCOMMODATION_ASSIGNMENTS_ORDER_MUTATION,
  ADD_TRAVEL_ACCOMMODATIONS_MUTATION,
  REMOVE_TRAVEL_ACCOMMODATIONS_MUTATION,
  CREATE_TRAVEL_ACCOMMODATIONS_MUTATION,
  DELETE_TRAVEL_ACCOMMODATIONS_MUTATION,
  UPDATE_TRAVEL_ACCOMMODATIONS_ORDER_MUTATION,
  ADD_TRAVEL_DESTINATIONS_MUTATION,
  REMOVE_TRAVEL_DESTINATIONS_MUTATION,
  CREATE_TRAVEL_DESTINATIONS_MUTATION,
  DELETE_TRAVEL_DESTINATIONS_MUTATION,
  UPDATE_TRAVEL_DESTINATIONS_ORDER_MUTATION,
  ADD_TRAVEL_TESTIMONIALS_MUTATION,
  REMOVE_TRAVEL_TESTIMONIALS_MUTATION,
  CREATE_TRAVEL_TESTIMONIALS_MUTATION,
  DELETE_TRAVEL_TESTIMONIALS_MUTATION,
  UPDATE_TRAVEL_TESTIMONIALS_ORDER_MUTATION,
  ADD_TRAVEL_TRAVEL_EXTENSIONS_MUTATION,
  REMOVE_TRAVEL_TRAVEL_EXTENSIONS_MUTATION,
  CREATE_TRAVEL_TRAVEL_EXTENSIONS_MUTATION,
  DELETE_TRAVEL_TRAVEL_EXTENSIONS_MUTATION,
  UPDATE_TRAVEL_TRAVEL_EXTENSIONS_ORDER_MUTATION,
  ADD_TRAVEL_FAQS_MUTATION,
  REMOVE_TRAVEL_FAQS_MUTATION,
  CREATE_TRAVEL_FAQS_MUTATION,
  DELETE_TRAVEL_FAQS_MUTATION,
  UPDATE_TRAVEL_FAQS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const travelRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_TRAVEL_QUERY, variables).then((data: any) => {
      return hydrateObject(data.travel)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_TRAVELS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.travels)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_TRAVEL_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.travel),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_TRAVELS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.travels),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(TRAVELS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([TRAVELS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_TRAVEL_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.travel)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_TRAVEL_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.travel)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_TRAVEL_MUTATION, variables).then((data: any) => {
      return data.deleteTravel
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_TRAVEL_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTravel
    })
  },

  assignTenantSettings: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tenantSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_TENANT_SETTINGS_MUTATION, variables)
  },

  unassignTenantSettings: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tenantSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_TENANT_SETTINGS_MUTATION, variables)
  },

  createTenantSettings: async (id: string, items: Array<travelObjectTenantSettingsObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tenantSettings: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_TENANT_SETTINGS_MUTATION, variables)
  },

  deleteTenantSettings: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tenantSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_TENANT_SETTINGS_MUTATION, variables)
  },

  updateTenantSettingsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        tenantSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_TENANT_SETTINGS_ORDER_MUTATION, variables)
  },

  assignHighlights: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        highlightIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_HIGHLIGHTS_MUTATION, variables)
  },

  unassignHighlights: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        highlightIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_HIGHLIGHTS_MUTATION, variables)
  },

  createHighlights: async (id: string, items: Array<travelHighlightObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        highlights: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_HIGHLIGHTS_MUTATION, variables)
  },

  deleteHighlights: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        highlightIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_HIGHLIGHTS_MUTATION, variables)
  },

  updateHighlightsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        highlightIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_HIGHLIGHTS_ORDER_MUTATION, variables)
  },

  assignPartners: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partnerIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_PARTNERS_MUTATION, variables)
  },

  unassignPartners: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partnerIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_PARTNERS_MUTATION, variables)
  },

  createPartners: async (id: string, items: Array<travelPartnerObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partners: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_PARTNERS_MUTATION, variables)
  },

  deletePartners: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partnerIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_PARTNERS_MUTATION, variables)
  },

  updatePartnersOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partnerIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_PARTNERS_ORDER_MUTATION, variables)
  },

  assignTravelGuides: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelGuideIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_TRAVEL_GUIDES_MUTATION, variables)
  },

  unassignTravelGuides: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelGuideIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_TRAVEL_GUIDES_MUTATION, variables)
  },

  createTravelGuides: async (id: string, items: Array<travelGuideObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelGuides: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_TRAVEL_GUIDES_MUTATION, variables)
  },

  deleteTravelGuides: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelGuideIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_TRAVEL_GUIDES_MUTATION, variables)
  },

  updateTravelGuidesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelGuideIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_TRAVEL_GUIDES_ORDER_MUTATION, variables)
  },

  assignTeams: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        teamIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_TEAMS_MUTATION, variables)
  },

  unassignTeams: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        teamIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_TEAMS_MUTATION, variables)
  },

  createTeams: async (id: string, items: Array<travelTeamObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        teams: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_TEAMS_MUTATION, variables)
  },

  deleteTeams: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        teamIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_TEAMS_MUTATION, variables)
  },

  updateTeamsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        teamIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_TEAMS_ORDER_MUTATION, variables)
  },

  assignAccommodationAssignments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_ACCOMMODATION_ASSIGNMENTS_MUTATION, variables)
  },

  unassignAccommodationAssignments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_ACCOMMODATION_ASSIGNMENTS_MUTATION, variables)
  },

  createAccommodationAssignments: async (id: string, items: Array<travelAccommodationAssignmentObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAssignments: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_ACCOMMODATION_ASSIGNMENTS_MUTATION, variables)
  },

  deleteAccommodationAssignments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_ACCOMMODATION_ASSIGNMENTS_MUTATION, variables)
  },

  updateAccommodationAssignmentsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_ACCOMMODATION_ASSIGNMENTS_ORDER_MUTATION, variables)
  },

  assignAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_ACCOMMODATIONS_MUTATION, variables)
  },

  unassignAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_ACCOMMODATIONS_MUTATION, variables)
  },

  createAccommodations: async (id: string, items: Array<accommodationObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodations: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_ACCOMMODATIONS_MUTATION, variables)
  },

  deleteAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_ACCOMMODATIONS_MUTATION, variables)
  },

  updateAccommodationsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_ACCOMMODATIONS_ORDER_MUTATION, variables)
  },

  assignDestinations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_DESTINATIONS_MUTATION, variables)
  },

  unassignDestinations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_DESTINATIONS_MUTATION, variables)
  },

  createDestinations: async (id: string, items: Array<travelDestinationObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinations: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_DESTINATIONS_MUTATION, variables)
  },

  deleteDestinations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_DESTINATIONS_MUTATION, variables)
  },

  updateDestinationsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_DESTINATIONS_ORDER_MUTATION, variables)
  },

  assignTestimonials: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        testimonialIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_TESTIMONIALS_MUTATION, variables)
  },

  unassignTestimonials: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        testimonialIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_TESTIMONIALS_MUTATION, variables)
  },

  createTestimonials: async (id: string, items: Array<travelTestimonialObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        testimonials: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_TESTIMONIALS_MUTATION, variables)
  },

  deleteTestimonials: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        testimonialIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_TESTIMONIALS_MUTATION, variables)
  },

  updateTestimonialsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        testimonialIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_TESTIMONIALS_ORDER_MUTATION, variables)
  },

  assignTravelExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelExtensionIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_TRAVEL_EXTENSIONS_MUTATION, variables)
  },

  unassignTravelExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelExtensionIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_TRAVEL_EXTENSIONS_MUTATION, variables)
  },

  createTravelExtensions: async (id: string, items: Array<travelExtensionObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelExtensions: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_TRAVEL_EXTENSIONS_MUTATION, variables)
  },

  deleteTravelExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelExtensionIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_TRAVEL_EXTENSIONS_MUTATION, variables)
  },

  updateTravelExtensionsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelExtensionIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_TRAVEL_EXTENSIONS_ORDER_MUTATION, variables)
  },

  assignFaqs: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        faqIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVEL_FAQS_MUTATION, variables)
  },

  unassignFaqs: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        faqIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVEL_FAQS_MUTATION, variables)
  },

  createFaqs: async (id: string, items: Array<travelFaqObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        faqs: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVEL_FAQS_MUTATION, variables)
  },

  deleteFaqs: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        faqIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVEL_FAQS_MUTATION, variables)
  },

  updateFaqsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        faqIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVEL_FAQS_ORDER_MUTATION, variables)
  },
}

export default travelRepository
